import { useState, useEffect } from "react";
import logoImg from "../../../assets/images/logo.png";

interface NavbarProps {
  theme: "dark" | "light";
  onThemeToggle: () => void;
  lang: "fr" | "en" | "ar";
  onLangChange: (l: "fr" | "en" | "ar") => void;
}

const labels: Record<string, Record<string, string>> = {
  fr: { features: "Fonctionnalités", how: "Comment ça marche", download: "Télécharger", cta: "Télécharger" },
  en: { features: "Features", how: "How it works", download: "Download", cta: "Download" },
  ar: { features: "المميزات", how: "كيف يعمل", download: "تحميل", cta: "تحميل" },
};

export default function Navbar({ theme, onThemeToggle, lang, onLangChange }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const t = labels[lang];
  const isDark = theme === "dark";

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const navBg = scrolled
    ? isDark
      ? "bg-black/72 backdrop-blur-[40px] saturate-150 border-b border-white/10"
      : "bg-white/80 backdrop-blur-[40px] saturate-150 border-b border-black/10"
    : "bg-transparent";

  const textColor = isDark ? "text-white" : "text-gray-900";
  const mutedColor = isDark ? "text-white/70" : "text-gray-600";

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`} dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <a href="#" className={`flex items-center gap-2 cursor-pointer whitespace-nowrap ${textColor}`} onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
          <img src={logoImg} alt="Mawthoq Logo" className="w-8 h-8 object-contain rounded-lg" />
          <span className="text-lg font-semibold tracking-tight">موثوق</span>
        </a>

        {/* Center nav links */}
        <div className={`hidden md:flex items-center gap-8 ${mutedColor}`}>
          {[
            { label: t.features, id: "features" },
            { label: t.how, id: "how-it-works" },
            { label: t.download, id: "download" },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className={`text-sm font-medium hover:${isDark ? "text-white" : "text-gray-900"} transition-colors duration-200 cursor-pointer whitespace-nowrap relative group`}
            >
              {item.label}
              <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-[#007AFF] group-hover:w-full transition-all duration-300 rounded-full" />
            </button>
          ))}
        </div>

        {/* Right controls */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language switcher */}
          <div className={`flex items-center rounded-full p-0.5 text-xs font-semibold ${isDark ? "bg-white/10 border border-white/10" : "bg-black/10 border border-black/10"}`}>
            {(["fr", "en", "ar"] as const).map(l => (
              <button
                key={l}
                onClick={() => onLangChange(l)}
                className={`px-3 py-1 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  lang === l
                    ? isDark ? "bg-white text-black shadow-sm" : "bg-black text-white shadow-sm"
                    : isDark ? "text-white/70 hover:text-white" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {l === "fr" ? "FR" : l === "en" ? "EN" : "عربي"}
              </button>
            ))}
          </div>

          {/* Theme toggle */}
          <button
            onClick={onThemeToggle}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer ${isDark ? "bg-white/10 border border-white/10 text-white hover:bg-white/20" : "bg-black/10 border border-black/10 text-gray-700 hover:bg-black/20"}`}
          >
            <i className={`${isDark ? "ri-sun-line" : "ri-moon-line"} text-base`} />
          </button>

          {/* CTA */}
          <button
            onClick={() => scrollTo("download")}
            className="px-5 py-2 rounded-full bg-[#007AFF] text-white text-sm font-semibold hover:bg-[#0066DD] transition-all duration-200 cursor-pointer whitespace-nowrap shadow-lg shadow-[#007AFF]/30 active:scale-95"
          >
            {t.cta}
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full cursor-pointer ${isDark ? "text-white bg-white/10" : "text-gray-900 bg-black/10"}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-lg`} />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className={`md:hidden px-6 pb-6 pt-2 flex flex-col gap-4 ${isDark ? "bg-black/90 backdrop-blur-xl" : "bg-white/90 backdrop-blur-xl"}`}>
          {[
            { label: t.features, id: "features" },
            { label: t.how, id: "how-it-works" },
            { label: t.download, id: "download" },
          ].map(item => (
            <button key={item.id} onClick={() => scrollTo(item.id)} className={`text-left text-sm font-medium cursor-pointer ${mutedColor}`}>
              {item.label}
            </button>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <div className={`flex items-center rounded-full p-0.5 text-xs font-semibold ${isDark ? "bg-white/10 border border-white/10" : "bg-black/10 border border-black/10"}`}>
              {(["fr", "en", "ar"] as const).map(l => (
                <button key={l} onClick={() => onLangChange(l)} className={`px-3 py-1 rounded-full transition-all duration-200 cursor-pointer ${lang === l ? isDark ? "bg-white text-black" : "bg-black text-white" : isDark ? "text-white/70" : "text-gray-600"}`}>
                  {l === "fr" ? "FR" : l === "en" ? "EN" : "عربي"}
                </button>
              ))}
            </div>
            <button onClick={onThemeToggle} className={`w-9 h-9 rounded-full flex items-center justify-center cursor-pointer ${isDark ? "bg-white/10 text-white" : "bg-black/10 text-gray-700"}`}>
              <i className={`${isDark ? "ri-sun-line" : "ri-moon-line"} text-base`} />
            </button>
          </div>
          <button onClick={() => scrollTo("download")} className="w-full py-2.5 rounded-full bg-[#007AFF] text-white text-sm font-semibold cursor-pointer whitespace-nowrap">
            {t.cta}
          </button>
        </div>
      )}
    </nav>
  );
}
